import {
  AuctionBiddingEngine,
  DateValue,
  EAuctionBiddingStatus,
  EAuctionStatus,
  EAuctionType,
  ELotStatus,
  LotStatus,
  PaginatedBidResponse,
} from '@bonhams1793/bonhams-typescript'
import * as Sentry from '@sentry/nextjs'
import { useQueryClient } from '@tanstack/react-query'
import { toZonedTime } from 'date-fns-tz'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useStore } from '@nx/app-store'
import {
  BidStatus,
  BiddingDropdown,
  BiddingWidgetSkeleton,
  ChannelState,
  FlipClockTimer,
  LotChannelLastPayload,
  LotClosingTimer,
  OutbidMessage,
  SubscriptionFailed,
  UserLot,
  buildLotChannelName,
  getNextBid,
  useLotChannel,
} from '@nx/bidding-engine'
import { decodeHtml } from '@nx/decode-html'
import { usePusher } from '@nx/external/pusher'
import { showToast } from '@nx/fire/toast'
import {
  buildPriceString,
  canAuctionBeRegisteredFor,
  canLotBeBidOn,
} from '@nx/helpers'
import { getTranslate } from '@nx/translations'

import { useGetUser } from '@web/helpers/api'
import { usePatchBid } from '@web/helpers/api/bidding/patchBid'
import { usePostBid } from '@web/helpers/api/bidding/postBid'
import { useGetUserRegistrations } from '@web/helpers/api/user'
import { useBiddingConnectionNotification } from '@web/helpers/hooks/useBiddingConnectionNotification/useBiddingConnectionNotification'

import {
  StyledBiddingButton,
  StyledBiddingContainer,
} from './BiddingWidget.styles'
import translate from './BiddingWidget.translate.json'

const { text } = getTranslate(translate)

export function BiddingWidget({
  auction: { auctionId, startDate, biddingStatus, auctionStatus, auctionType },
  biddingStyle,
  lot: {
    currencySymbol,
    hammerTime,
    isExhibition,
    isPreviewLot,
    isToBeSold,
    lotUniqueId,
  },
  showOutbidMessage,
  timerStyle,
  onLotFinished,
  refreshCallback,
}: BiddingWidgetProps) {
  const queryClient = useQueryClient()
  const user = useGetUser()
  const { userRegistrations } = useGetUserRegistrations({
    clientNo: user.data?.customerId,
  })
  const channelName = buildLotChannelName(lotUniqueId)
  const channel: ChannelState | undefined = useStore(
    useShallow((store) => store.channels[channelName])
  )
  const channelLastPayload: LotChannelLastPayload | undefined =
    channel?.lastPayload as LotChannelLastPayload
  const userLotData: UserLot | undefined = useStore(
    useShallow((store) => store.user.lots[lotUniqueId])
  )
  const storeActions = useStore(
    useShallow((store) => ({
      addRegistration: store.addRegistration,
      removeRegistration: store.removeRegistration,
      updateChannelPayload: store.updateChannelPayload,
      updateChannelSubscriptionStatus: store.updateChannelSubscriptionStatus,
      addLot: store.addLot,
      updateLot: store.updateLot,
      removeLot: store.removeLot,
    }))
  )

  const { pusherConnectionStatus, subscribe, unsubscribe } = usePusher()
  useLotChannel({
    channelName,
    subscribe,
    unsubscribe,
    addRegistration: storeActions.addRegistration,
    removeRegistration: storeActions.removeRegistration,
    updateChannelPayload: storeActions.updateChannelPayload,
    updateChannelSubscriptionStatus:
      storeActions.updateChannelSubscriptionStatus,
  })
  const postBid = usePostBid()
  const patchBid = usePatchBid()

  const [nextBid, setNextBid] = useState<number>(0)
  const [pauseAnimation, setPauseAnimation] = useState<boolean>(false)
  const [buttonRenderCounter, setButtonRenderCounter] = useState(0)

  useBiddingConnectionNotification({ biddingEngine: 'bonhams_bidding_engine' })

  const auctionCanBeBidOn = canAuctionBeRegisteredFor({
    auctionType: auctionType,
    auctionStatus: auctionStatus,
    biddingStatus: biddingStatus,
    biddingEngine: AuctionBiddingEngine.BBE,
  })

  const lotCanBeBidOn = canLotBeBidOn({
    auctionType: auctionType,
    biddableFrom: startDate,
    biddingEngine: AuctionBiddingEngine.BBE,
    isExhibition: isExhibition,
    isPreview: isPreviewLot,
    isToBeSold:
      channelLastPayload?.type === 'lot.finished' ? false : isToBeSold,
  })

  const isLoading = Boolean(
    channel?.subscriptionStatus !== 'SUBSCRIBED' && !channelLastPayload
  )
  const isLoggedIn = !!user.data
  const userRegistration = userRegistrations.find(
    (registration) => String(registration.iSaleNo) === auctionId
  )

  const userCanBid =
    isLoggedIn &&
    userRegistration &&
    userRegistration.approvedToBid &&
    channelLastPayload?.type !== 'lot.finished' &&
    auctionCanBeBidOn &&
    lotCanBeBidOn
  const isOutbid = Boolean(
    userLotData &&
      userLotData.bidAmount &&
      channelLastPayload &&
      channelLastPayload.currentBid &&
      'currentPaddleNo' in channelLastPayload &&
      userLotData.paddleNo !== channelLastPayload.currentPaddleNo &&
      userLotData.bidAmount <= channelLastPayload.currentBid
  )
  const isWinningBidder = Boolean(
    userLotData &&
      channelLastPayload &&
      'currentPaddleNo' in channelLastPayload &&
      userLotData.paddleNo === channelLastPayload.currentPaddleNo
  )
  const decodedCurrencySymbol = decodeHtml(currencySymbol)

  let currentBidValue = channelLastPayload?.currentBid

  if (
    channelLastPayload &&
    'startingPrice' in channelLastPayload &&
    currentBidValue === null
  ) {
    currentBidValue = channelLastPayload.startingPrice
  }

  let liveHammerTime = undefined

  if (hammerTime) {
    liveHammerTime = toZonedTime(
      channelLastPayload && 'endingDate' in channelLastPayload
        ? channelLastPayload.endingDate
        : hammerTime.datetime,
      hammerTime.timezone.iana
    )
  }

  const isCurrentBidIncluded = Boolean(
    channelLastPayload &&
      'startingPrice' in channelLastPayload &&
      channelLastPayload.currentBid === null
  )

  useEffect(() => {
    if (
      channelLastPayload &&
      'bidIncrementPolicy' in channelLastPayload &&
      currentBidValue
    ) {
      setNextBid((currentState) => {
        const nextBid = getNextBid({
          biddingPolicy: channelLastPayload.bidIncrementPolicy,
          currentBidValue: currentBidValue,
          isCurrentBidIncluded: isCurrentBidIncluded,
          isWinningBidder,
          maxBidValue: userLotData?.bidAmount,
        })

        if (channelLastPayload.type === 'lot.cancelBid') {
          return nextBid
        }

        return nextBid > currentState ? nextBid : currentState
      })
    }
  }, [
    channelLastPayload,
    currentBidValue,
    isWinningBidder,
    userLotData?.bidAmount,
    isCurrentBidIncluded,
  ])

  useEffect(() => {
    if (channelLastPayload?.type === 'lot.finished') {
      if (
        'status' in channelLastPayload &&
        channelLastPayload.status === 'SOLD'
      ) {
        storeActions.updateLot(lotUniqueId, {
          won: userLotData?.paddleNo === channelLastPayload.currentPaddleNo,
        })
      }
      onLotFinished?.({
        hammerPrice: channelLastPayload.currentBid,
        hammerPremium: channelLastPayload.hammerPremium,
        status:
          {
            SOLD: LotStatus.sold,
            UNSOLD: LotStatus.unsold,
            WITHDRAWN: LotStatus.withdrawn,
          }[channelLastPayload.status] ?? LotStatus.unsold,
      })
    }
  }, [
    channelLastPayload,
    lotUniqueId,
    storeActions,
    userLotData,
    onLotFinished,
  ])

  useEffect(() => {
    if (
      channelLastPayload?.type === 'lot.cancelBid' &&
      'cancelPaddleNo' in channelLastPayload &&
      channelLastPayload.cancelPaddleNo === userLotData?.paddleNo
    ) {
      queryClient
        .invalidateQueries({ queryKey: ['activeBids'], exact: false })
        .then(() => {
          const activeBids = queryClient.getQueryData<PaginatedBidResponse>([
            'activeBids',
            user.data?.customerId,
          ])

          const lotBid = activeBids?.bids.find(
            (bid) => bid.lot_id === lotUniqueId
          )

          if (lotBid) {
            storeActions.updateLot(lotUniqueId, {
              bidAmount: lotBid.amount,
              bidId: lotBid.max_bid_id,
            })
          } else {
            storeActions.removeLot(lotUniqueId)
          }
        })
        .catch((error) => {
          Sentry.captureException(error, {
            extra: {
              message:
                'Unable to refetch user invoices after their bid was cancelled',
            },
          })
          storeActions.removeLot(lotUniqueId)
        })
    }
  }, [
    channelLastPayload,
    lotUniqueId,
    storeActions,
    userLotData,
    queryClient,
    user.data,
    channelName,
  ])

  if (!auctionCanBeBidOn || !lotCanBeBidOn) return null

  if (
    channel?.subscriptionStatus === 'FAILED' ||
    pusherConnectionStatus === 'DISCONNECTED'
  ) {
    return (
      <StyledBiddingContainer>
        <SubscriptionFailed refreshCallback={() => refreshCallback()} />
      </StyledBiddingContainer>
    )
  }

  return (
    <StyledBiddingContainer>
      {showOutbidMessage && isOutbid ? <OutbidMessage /> : null}

      {liveHammerTime ? (
        timerStyle === 'flipClock' ? (
          <FlipClockTimer endDate={liveHammerTime} />
        ) : (
          <LotClosingTimer endDate={liveHammerTime} />
        )
      ) : null}

      {isLoading ? (
        <BiddingWidgetSkeleton />
      ) : channelLastPayload ? (
        <>
          <BidStatus
            userLotData={userLotData}
            biddingEvent={channelLastPayload}
            currencySymbol={currencySymbol}
            isWinningBidder={isWinningBidder}
            showOutbidMessage={showOutbidMessage}
          />

          {biddingStyle === 'dropdown'
            ? userCanBid &&
              'bidIncrementPolicy' in channelLastPayload &&
              currentBidValue && (
                <BiddingDropdown
                  biddingPolicy={channelLastPayload.bidIncrementPolicy}
                  currencySymbol={currencySymbol}
                  currentBid={currentBidValue}
                  nextBid={nextBid}
                  isWinningBidder={isWinningBidder}
                  maxBidValue={userLotData?.bidAmount}
                  numberOfOptions={50}
                  selectBidCallback={setNextBid}
                  isCurrentBidIncluded={isCurrentBidIncluded}
                />
              )
            : null}

          {userCanBid && nextBid ? (
            biddingStyle === 'nextIncrement' && isWinningBidder ? null : (
              <StyledBiddingButton
                key={buttonRenderCounter}
                bidValueInvalid={nextBid === userLotData?.bidAmount}
                label={{
                  confirmation: text('confirm', {
                    price: buildPriceString({
                      currencySymbol: decodedCurrencySymbol,
                      lowPrice: nextBid,
                    }),
                  }),
                  initial: text('bid', {
                    price: buildPriceString({
                      currencySymbol: decodedCurrencySymbol,
                      lowPrice: nextBid,
                    }),
                  }),
                }}
                onConfirmClick={async () => {
                  setPauseAnimation(true)

                  if (userCanBid && userRegistration.iPaddleNo) {
                    if (isWinningBidder && userLotData && userLotData.bidId) {
                      await patchBid.mutateAsync(
                        {
                          amount: nextBid,
                          bidId: userLotData.bidId,
                        },
                        {
                          onSuccess: (_, params) =>
                            storeActions.updateLot(lotUniqueId, {
                              bidAmount: params.amount,
                              bidId: params.bidId,
                            }),
                          onError: () => {
                            showToast.error(text('bidFailed'))
                            setButtonRenderCounter((state) => state + 1)
                            patchBid.reset()
                          },
                        }
                      )
                    } else {
                      await postBid.mutateAsync(
                        {
                          amount: nextBid,
                          lot_id: String(lotUniqueId),
                          bid_type: 'online',
                          cancelled: 0,
                          channel: 'web',
                        },
                        {
                          onSuccess: (bid) =>
                            storeActions.addLot({
                              auctionId,
                              lotUniqueId,
                              bidAmount: bid.max_bid.amount,
                              paddleNo: userRegistration.iPaddleNo,
                              won: false,
                              hasActiveBid: true,
                              bidId: bid.max_bid.max_bid_id,
                            }),
                          onError: () => {
                            showToast.error(text('bidFailed'))
                            setButtonRenderCounter((state) => state + 1)
                            postBid.reset()
                          },
                        }
                      )
                    }
                  }

                  setPauseAnimation(false)
                }}
                pauseAnimation={pauseAnimation}
                variant={biddingStyle === 'nextIncrement' ? 'compact' : 'large'}
              />
            )
          ) : null}
        </>
      ) : null}
    </StyledBiddingContainer>
  )
}

export interface BiddingWidgetProps {
  auction: {
    auctionId: string
    startDate?: DateValue
    biddingStatus: EAuctionBiddingStatus
    auctionStatus: EAuctionStatus
    auctionType: EAuctionType
  }
  biddingStyle: 'nextIncrement' | 'dropdown'
  lot: {
    currencySymbol: string
    hammerTime?: DateValue
    isExhibition: boolean
    isPreviewLot: boolean
    isToBeSold: boolean
    lotUniqueId: string
  }
  showOutbidMessage?: boolean
  timerStyle: 'text' | 'flipClock'
  onLotFinished?: ({
    hammerPrice,
    hammerPremium,
    status,
  }: {
    hammerPrice: number | null
    hammerPremium: number | null
    status: ELotStatus
  }) => void
  refreshCallback: () => void
}
